import React from 'react';
import { FieldError, FieldValues, UseFormRegisterReturn, UseFormTrigger } from 'react-hook-form';
import { defineMessages, FormattedMessage } from 'react-intl';

import classNames from 'classnames';
import * as yup from 'yup';

import { ErrorBoundary } from '@nxte-nl/error-boundary';

import { phoneNumberRegex } from '../../constants/regularExpressions';
import { CountryCode } from '../../types/apiEnums';
import {
  FormSelectPhoneCodeCountry,
} from '../FormSelectPhoneCodeCountry/FormSelectPhoneCodeCountry';
import {
  Input,
  InputState,
  InputType,
  BaseFormInputProps,
  FormInputMode,
} from '../Input/Input';
import { Typography } from '../Typography/Typography';
import styles from './FormInputPhoneNumber.module.css';

export enum FormInputPhoneNumberLabelName {
  phoneNumberLabel = 'phoneNumberLabel',
  phoneNumberRequiredLabel = 'phoneNumberRequiredLabel',
}

const messages = defineMessages({
  phoneNumberLabel: {
    id: 'src.components.FormInputPhoneNumber.phoneNumberLabel',
    defaultMessage: 'Phone number',
  },
  phoneNumberRequiredLabel: {
    id: 'src.components.FormInputPhoneNumber.phoneNumberRequiredLabel',
    defaultMessage: 'Phone number *',
  },
  phoneNumberEmpty: {
    id: 'src.components.FormInputPhoneNumber.phoneNumberEmpty',
    defaultMessage: 'Phone number is a required field',
  },
  invalidPhoneNumber: {
    id: 'src.components.FormInputPhoneNumber.invalidPhoneNumber',
    defaultMessage: 'Please enter valid phone number',
  },
});

export const FORM_INPUT_PHONE_NUMBER = 'phoneNumber';
export const FORM_INPUT_PHONE_CODE = 'phoneCode';

export const hooks = {
  useTestPhoneNumberRegex(originalRegex: RegExp, props: yup.TestContext) {
    if (props.options.context) {
      const SECOND_PART_MIN = 6;
      // starting min second part of phone number (9 - 3 which is 1st part from original regex)
      const SECOND_PART_MAX = 11;
      // starting max second part of phone number (14 - 3 which is 1st part from original regex)
      // for further subtraction based on phone code length
      const context = props.options.context as { phoneCode: string };
      const phoneCode = context.phoneCode.replace('+', '');
      const secondMin = (SECOND_PART_MIN - phoneCode.length).toString();
      const secondMax = (SECOND_PART_MAX - phoneCode.length).toString();
      return new RegExp(
        `${originalRegex.source
          .replace('5,8', `${secondMin},${secondMax}`)
        }`, // replacing of second part length in originalregex
      );
    }
    return originalRegex;
  },
};

export const phoneNumberValidationSchema = yup.string()
  .test(FORM_INPUT_PHONE_NUMBER, 'invalidPhoneNumber', (value, props) => {
    const phoneNumberRegexToCheck = hooks.useTestPhoneNumberRegex(phoneNumberRegex, props);
    if (value) {
      return yup.string().matches(phoneNumberRegexToCheck, 'invalidPhoneNumber').isValidSync(value);
    }

    return true;
  });

export const phoneNumberRequiredValidationSchema = yup.string()
  .required('phoneNumberEmpty')
  .test(FORM_INPUT_PHONE_NUMBER, 'invalidPhoneNumber', (value, props) => {
    if (value) {
      const phoneNumberRegexToCheck = hooks.useTestPhoneNumberRegex(phoneNumberRegex, props);
      return yup.string().matches(phoneNumberRegexToCheck, 'invalidPhoneNumber').isValidSync(value);
    }

    return true;
  });

export type FormInputPhoneNumberErrors =
  'phoneNumberEmpty'
  | 'invalidPhoneNumber';

export type FormFieldsPhoneNumber = {
  [FORM_INPUT_PHONE_NUMBER]: string;
};

export type FormFieldsPhoneCode = {
  [FORM_INPUT_PHONE_CODE]: string;
};

export const formFieldsPhoneNumberDefaultValue: FormFieldsPhoneNumber = {
  [FORM_INPUT_PHONE_NUMBER]: '',
};

export enum FormInputPhoneLabelPosition {
  left = 'left',
  top = 'top',
}

export type Props<T extends FieldValues,> = UseFormRegisterReturn & BaseFormInputProps & {
  error?: FieldError;
  formSelectWithInputClassName?: string;
  isDisabled?: boolean;
  isFormDirty: boolean;
  labelClassName?: string;
  labelContainerClassname?: string;
  labelName?: FormInputPhoneNumberLabelName;
  labelPosition?: FormInputPhoneLabelPosition;
  onCountryChange: (countryCode: CountryCode, phoneCode: string) => void;
  phoneCode: string;
  prefillCountryCode?: CountryCode | null;
  selectHeaderClassName?: string;
  trigger: UseFormTrigger<T>;
};

export const FormInputPhoneNumber = React.memo(React.forwardRef(<T extends FieldValues,>(
  props: Props<T>,
  ref: React.ForwardedRef<HTMLInputElement | HTMLTextAreaElement>,
) => {
  const {
    error,
    labelName = FormInputPhoneNumberLabelName.phoneNumberRequiredLabel,
    isDisabled = false,
    onCountryChange,
    phoneCode,
    prefillCountryCode,
    trigger,
    isFormDirty,
    labelClassName,
    labelContainerClassname,
    formSelectWithInputClassName,
    selectHeaderClassName,
    labelPosition = FormInputPhoneLabelPosition.top,
    ...rest
  } = props;

  return (
    <div className={classNames(styles.formInputWrapper, {
      [styles.leftLabelPosotion]: labelPosition === FormInputPhoneLabelPosition.left,
      [styles.topLabelPosition]: labelPosition === FormInputPhoneLabelPosition.top,
    })}>
      <div
        className={classNames(styles.labelContainer, labelContainerClassname)}
      >
        <Typography
          component='label'
          className={classNames(styles.inputLabel, labelClassName)}
        >
          {<FormattedMessage {...messages[labelName]} />}
        </Typography>
      </div>
      <div className={classNames(styles.phoneInputGroup, formSelectWithInputClassName)}>
        <ErrorBoundary>
          <FormSelectPhoneCodeCountry
            onChange={onCountryChange}
            defaultValue={prefillCountryCode}
            trigger={trigger as unknown as UseFormTrigger<FieldValues>}
            isFormDirty={isFormDirty}
            selectHeaderClassName={selectHeaderClassName}
          />
        </ErrorBoundary>
        <Input
          inputType={InputType.Input}
          state={
            isDisabled && InputState.Disabled
          || error && InputState.Error
          || undefined
          }
          errorMessage={
            error
          && <FormattedMessage
            {...messages[error?.message as FormInputPhoneNumberErrors]}
          />
          }
          type='number'
          inputClassName={styles.phone}
          inputMode={FormInputMode.numeric}
          prefixLabel={
            <Typography className={styles.countryCode}>
              {phoneCode}
            </Typography>}
          {...rest}
          ref={ref}
        />
      </div>
    </div>
  );
}));

FormInputPhoneNumber.displayName = 'FormInputPhoneNumber';
